import { supabase } from "@/services/supabase/client";
import { Database } from "@/services/supabase/types";

export type FeedbackType = "feature" | "bug" | "comment";
export type FeedbackStatus = "proposed" | "in-progress" | "completed";

type DbFeedback = Database["public"]["Tables"]["feedback"];
export interface FeedbackRow extends Omit<DbFeedback["Row"], "type" | "status"> {
  type: FeedbackType;
  status: FeedbackStatus;
}
export interface FeedbackInsert extends Omit<DbFeedback["Insert"], "type" | "status"> {
  type: FeedbackType;
  status?: FeedbackStatus;
}
export interface FeedbackUpdate extends Omit<DbFeedback["Update"], "type" | "status"> {
  type?: FeedbackType;
  status?: FeedbackStatus;
}

export type FetchedFeedback = Pick<FeedbackRow, "id" | "description" | "type" | "status" | "votes">;

async function getFeedbacks(): Promise<FetchedFeedback[]> {
  const { data, error } = await supabase
    .from("feedback")
    .select("id, description, type, status, votes")
    .in("type", ["feature", "bug"])
    .order("votes", { ascending: false });

  if (error) throw error;

  return data as FetchedFeedback[];
}

async function addFeedback(feedback: FeedbackInsert): Promise<FetchedFeedback | null> {
  const { data, error } = await supabase
    .from("feedback")
    .insert(feedback)
    .select("id, description, type, status, votes")
    .single();

  if (error) throw error;

  return data as FetchedFeedback;
}

async function toggleVote(feedbackId: number): Promise<string[] | null> {
  const { data, error } = await supabase.functions.invoke("toggle-feedback-vote", {
    body: { feedbackId },
  });

  if (error) throw error;

  return data.votes;
}

export const feedbackFetchers = {
  getFeedbacks,
  addFeedback,
  toggleVote,
};
